import { inject, injectable, interfaces } from 'inversify';
import { clamp } from 'lodash/fp';
import { action, IReactionDisposer, makeObservable, reaction } from 'mobx';
import { ACDLError } from '../../../../models/error.model';
import { IProjectHubService } from '../../../../services/projectHub.service';
import { BRUSH_MODE, IBrushTypeOptions, IImage } from '../../annotations.interface';
import { IAnnotationsStore } from '../../annotations.store';
import { IAnnotationUiStore } from '../../annotationUi.store';
import { DefaultConfiguration } from '../../components/DrawControl.models/Brush/BrushConfig';
import { IFreeDrawSegmentationService } from '../../freeDrawSegmentation.service';
import { IAnnotationTypeStore } from './annotationType.store';
import { AnnotationToolType } from './models/annotationToolType';
import { IAnnotationType } from './models/annotationType';
import { IAnnotationTypeOptions } from './models/annotationTypeOptions';
import { GetAnnotationTypesRequest, IGetAnnotationTypesService } from './services/getAnnotationTypesService';

const clampDiameter = clamp(DefaultConfiguration.minRadius * 2, DefaultConfiguration.maxRadius * 2);

export interface IAnnotationTypeBl {
  // Interacting with annotationService to avoid cross reference;
  onDeselectSegmentationInKeyBinding: (() => void) | undefined;
  whenImageIsLoaded: (() => Promise<IImage>) | undefined;

  // SetUp
  creationStarted(projectId: string): Promise<void>;
  reviewStarted(projectId: string): Promise<void>;
  freeAccessStarted(projectId: string, resetSelection: boolean): Promise<void>;
  previewStarted(annotationTypes: IAnnotationType[]): Promise<void>;

  // Replacing TearDown
  creationFinished(): void;
  reviewFinished(): void;
  previewFinished(): void;
  freeAccessFinished(): void;

  // Refresh annotationTypesIfNeeded
  handleSubmitAnnotationInFreeAccess(): void;
  handleFinishWorkWithImageInCreation(): void;
  handleDisplayNextImageInReview(): void;
  handleMoveToNextSegmentation(): void;
  handleSegmentationAnswerAddedCommandRedo(): void;
  handleSubmitReview(): Promise<void>;
  handleSubmitFixInFreeAccess(): Promise<void>;
  handleSaveAnnotationsInCreation(): Promise<void>;
  handleSubmitAnnotationInCreation(): Promise<void>;
  handleInvalidSubmitAnnotationInFreeAccess(): Promise<void>;
  handleAnnotationRemoveInDrawingContainer(): Promise<void>;
  handleSegmentationTapInSegmetationContainer(): Promise<void>;
  handleWillUnmountInAnnotationTypeHotKeyContainer(): Promise<void>;
  handleSelectedAnnotationDeleteInAnnotationTypeContainer(): Promise<void>;
  handleSegmentationDeselectedInSegmentationContainer(): Promise<boolean>;
  handleAnnotationTypeSelectedInAnnotationTypeContainer(annotationTypeId: string): Promise<void>;
  handleSegmentationClickedInSegmentationContainer(annotationTypeId?: string): Promise<boolean>;
  handleReject(): Promise<void>;

  getShortcutKey(order: number): string | undefined;

  setAnnotationTypeOptions(projectId: string, annotationTypeOptions: IAnnotationTypeOptions): void;
  unhideAllAnnotationTypeOptions(projectId: string): void;
  setDefaultAnnotationTypeOptions(projectId: string, annotationTypes: IAnnotationType[], image?: IImage): void;
}

export namespace IAnnotationTypeBl {
  export const $: interfaces.ServiceIdentifier<IAnnotationTypeBl> = Symbol('ANNOTATION_TYPE_SERVICE');
}

@injectable()
export class AnnotationTypeBl implements IAnnotationTypeBl {
  eraserReactionDispose: IReactionDisposer;
  private projectId?: string;

  constructor(
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore,
    @inject(IAnnotationUiStore.$) private readonly uiStore: IAnnotationUiStore,
    @inject(IProjectHubService.$) private readonly projectHubService: IProjectHubService,
    @inject(IAnnotationTypeStore.$) private readonly store: IAnnotationTypeStore,
    @inject(IGetAnnotationTypesService.$) private readonly getAnnotationTypesService: IGetAnnotationTypesService,
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService
  ) {
    makeObservable(this);
    this.eraserReactionDispose = reaction(
      () => this.freeDrawSegmentationService.freeDrawInProgress || this.freeDrawSegmentationService.freeDrawSelected,
      this.toggleEraser
    );
  }

  // Interacting with annotationService so that there is no cross reference.
  onDeselectSegmentationInKeyBinding: (() => void) | undefined = undefined;
  whenImageIsLoaded: (() => Promise<IImage>) | undefined = undefined;

  async reviewStarted(projectId: string): Promise<void> {
    await this.start(projectId);
  }

  async creationStarted(projectId: string): Promise<void> {
    await this.start(projectId);
  }

  async freeAccessStarted(projectId: string, resetSelection: boolean): Promise<void> {
    await this.start(projectId, resetSelection);
  }

  async previewStarted(annotationTypes: IAnnotationType[]): Promise<void> {
    this.setAnnotationTypes(annotationTypes);
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  private async start(projectId: string, resetSelection: boolean = true): Promise<void> {
    if (this.projectId === projectId) {
      this.refreshAnnotationTypes();
    } else {
      this.projectId = projectId;
      await this.downloadAnnotationTypes(projectId);
    }
    if (resetSelection) {
      await this.setActiveAnnotationType(AnnotationToolType.SELECT);
    }
  }

  creationFinished(): void {
    this.finish();
  }

  reviewFinished(): void {
    this.finish();
  }

  freeAccessFinished(): void {
    this.finish();
  }

  previewFinished(): void {
    this.finish();
  }

  private finish(): void {
    this.store.clean();
    this.projectId = undefined;
  }

  handleDisplayNextImageInReview(): void {
    this.refreshAnnotationTypes();
  }

  handleFinishWorkWithImageInCreation(): void {
    this.refreshAnnotationTypes();
  }

  handleSubmitAnnotationInFreeAccess(): void {
    this.refreshAnnotationTypes();
  }

  dispose(): void {
    this.eraserReactionDispose();
  }

  async handleAnnotationRemoveInDrawingContainer(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleSegmentationTapInSegmetationContainer(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleWillUnmountInAnnotationTypeHotKeyContainer(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleAnnotationTypeSelectedInAnnotationTypeContainer(annotationTypeId: string): Promise<void> {
    if (this.store.activeAnnotationTypeId !== annotationTypeId) {
      await this.setActiveAnnotationType(annotationTypeId);
    }
  }

  async handleSelectedAnnotationDeleteInAnnotationTypeContainer(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleSaveAnnotationsInCreation(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  handleMoveToNextSegmentation(): void {
    this.store.setActiveAnnotationTypeId(undefined);
  }

  handleSegmentationAnswerAddedCommandRedo(): void {
    this.store.setActiveAnnotationTypeId(undefined);
  }

  async handleSubmitFixInFreeAccess(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleSubmitReview(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleReject(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleInvalidSubmitAnnotationInFreeAccess(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleSubmitAnnotationInCreation(): Promise<void> {
    await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  async handleKeyBindingAnnotationTypeSelected(annotationTypeId: string): Promise<boolean> {
    return await this.setActiveAnnotationType(annotationTypeId);
  }

  async handleSegmentationClickedInSegmentationContainer(annotationTypeId?: string): Promise<boolean> {
    return await this.setActiveAnnotationType(annotationTypeId);
  }

  async handleSegmentationDeselectedInSegmentationContainer(): Promise<boolean> {
    return await this.setActiveAnnotationType(AnnotationToolType.SELECT);
  }

  private async setActiveAnnotationType(annotationTypeId?: string): Promise<boolean> {
    if (!(await this.freeDrawSegmentationService.clearAsync())) {
      return false;
    }

    let activeAnnotationTypeId = annotationTypeId;
    if (this.store.activeAnnotationTypeId === annotationTypeId || annotationTypeId === AnnotationToolType.SELECT || annotationTypeId === undefined) {
      activeAnnotationTypeId = undefined;
    }
    this.store.setActiveAnnotationTypeId(activeAnnotationTypeId);
    return true;
  }

  private refreshAnnotationTypes(): void {
    if (this.projectId && this.projectHubService.didAnnotationTypesChangedFor(this.projectId)) {
      const result = this.projectHubService.popNewAnnotationTypesFor(this.projectId);
      this.setAnnotationTypes(result);
    }
  }

  private async downloadAnnotationTypes(projectId: string): Promise<void> {
    const request = new GetAnnotationTypesRequest(projectId);
    const result = await this.getAnnotationTypesService.requestAnnotationTypesAsync(request);

    if (result instanceof ACDLError) {
      throw result;
    }
    this.setAnnotationTypes(result.annotationTypes);
  }

  private setAnnotationTypes(annotationTypes: IAnnotationType[]): void {
    this.store.setAnnotationTypes(annotationTypes);
    this.buildKeyBinding(this.store.annotationTypes, () => {
      this.freeDrawSegmentationService.clear();
      this.onDeselectSegmentationInKeyBinding?.();
    });

    void this.whenImageIsLoaded?.().then((image: IImage) => {
      this.setDefaultAnnotationTypeOptions(this.annotationsStore.projectId, this.store.annotationTypes, image);
    });
  }

  setDefaultAnnotationTypeOptions(projectId: string, annotationTypes: IAnnotationType[], image?: IImage): void {
    const annotationTypesOptions: IAnnotationTypeOptions[] = [];

    annotationTypes.forEach((annotationType: IAnnotationType) => {
      let annotationToolOptions: IAnnotationTypeOptions = {
        id: annotationType.id,
        toolType: annotationType.selectorType,
        isVisible: !(this.store.hiddenAnnotationTypes.find((x) => x.projectId === projectId)?.annotationTypes.some((x) => x === annotationType.id) || false),
      };

      switch (annotationType.selectorType) {
        case AnnotationToolType.BRUSH:
          annotationToolOptions = {
            ...annotationToolOptions,
            brushDiameter: image ? clampDiameter(Math.round(0.05 * Math.min(image.width, image.height))) : DefaultConfiguration.radius * 2,
            brushMode: BRUSH_MODE.DRAW,
            eraserAvailable: this.freeDrawSegmentationService.freeDrawInProgress,
            onDiameterChange: (diameter: number) => {
              this.setAnnotationTypeDiameter(annotationType.id, diameter);
            },
          } as IBrushTypeOptions;
          break;
        case AnnotationToolType.SELECT:
        case AnnotationToolType.POINT:
        case AnnotationToolType.POLYGON:
        case AnnotationToolType.RECTANGLE:
        case AnnotationToolType.VECTOR:
        case AnnotationToolType.POLYLINE:
        case AnnotationToolType.ROTATEDRECTANGLE:
          break;
      }
      annotationTypesOptions.push(annotationToolOptions);
    });

    this.store.setAnnotationTypeOptions(annotationTypesOptions);
  }

  setAnnotationTypeOptions(projectId: string, annotationTypeOptions: IAnnotationTypeOptions): void {
    const index = this.store.annotationTypesOptions.findIndex((a) => a.id === annotationTypeOptions.id);
    if (index === -1) {
      return;
    }

    const annotationTypesOptions = this.store.annotationTypesOptions;
    Object.assign(annotationTypesOptions[index], annotationTypeOptions);

    this.store.setAnnotationTypeOptions([...annotationTypesOptions]);

    const storeIndex = this.store.hiddenAnnotationTypes.findIndex((x) => x.projectId === projectId);
    const annotationTypes = this.store.annotationTypesOptions.filter((x) => !x.isVisible).map((x) => x.id);

    if (storeIndex !== -1) {
      this.store.hiddenAnnotationTypes.splice(storeIndex, 1);
    }
    this.store.setHiddenAnnotationTypes([...this.store.hiddenAnnotationTypes, { projectId, annotationTypes }]);
  }

  @action.bound
  unhideAllAnnotationTypeOptions(projectId: string): void {
    const storeIndex = this.store.hiddenAnnotationTypes.findIndex((x) => x.projectId === projectId);
    if (storeIndex !== -1) {
      this.store.hiddenAnnotationTypes.splice(storeIndex, 1);
    }
    this.store.setHiddenAnnotationTypes([...this.store.hiddenAnnotationTypes]);

    const annotationTypesOptions = this.store.annotationTypesOptions;
    this.store.annotationTypesOptions.forEach((x) => (x.isVisible = true));
    this.store.setAnnotationTypeOptions([...annotationTypesOptions]);
  }

  @action.bound
  setAnnotationTypeDiameter(id: string, diameter: number): void {
    const annotationTypeOptions = this.store.annotationTypesOptions.find((o) => o.id === id) as IBrushTypeOptions;
    if (!annotationTypeOptions) {
      return;
    }

    annotationTypeOptions.brushDiameter = diameter;
    this.store.setAnnotationTypeOptions([...this.store.annotationTypesOptions, annotationTypeOptions]);
  }

  @action
  toggleEraser = (value: boolean): void => {
    this.store.setAnnotationTypeOptions(
      this.store.annotationTypesOptions.map((options: IAnnotationTypeOptions) => {
        switch (options.toolType) {
          case AnnotationToolType.BRUSH: {
            const newOptions = options as IBrushTypeOptions;
            newOptions.eraserAvailable = value;
            if (!value) {
              newOptions.brushMode = BRUSH_MODE.DRAW;
            }
            return newOptions;
          }
          case AnnotationToolType.SELECT:
          case AnnotationToolType.POINT:
          case AnnotationToolType.POLYGON:
          case AnnotationToolType.RECTANGLE:
          case AnnotationToolType.VECTOR:
          case AnnotationToolType.POLYLINE:
          case AnnotationToolType.ROTATEDRECTANGLE:
          default:
            return options;
        }
      })
    );
  };

  buildKeyBinding(annotationTypes: IAnnotationType[], cb: () => void): void {
    const annotationTypeKeyHandlers: Map<string, () => Promise<void>> = new Map<string, () => Promise<void>>();

    annotationTypeKeyHandlers.set('`', async (): Promise<void> => {
      if (await this.handleKeyBindingAnnotationTypeSelected(AnnotationToolType.SELECT)) {
        cb();
      }
    });

    const sortedAnnotationTypes = annotationTypes.slice().sort((a: IAnnotationType, b: IAnnotationType) => a.order - b.order);

    sortedAnnotationTypes.forEach((m: IAnnotationType, i: number) => {
      const key = this.getShortcutKey(i);
      if (key) {
        annotationTypeKeyHandlers.set(key, async (): Promise<void> => {
          this.uiStore.setIsInValidation(false);
          if (await this.handleKeyBindingAnnotationTypeSelected(m.id)) {
            cb();
          }
        });
      }
    });

    this.store.setAnnotationTypeKeyMap(annotationTypeKeyHandlers);
  }

  getShortcutKey = (order: number): string | undefined => {
    if (order < 10) {
      if (order === 9) {
        return '0';
      }
      return String(order + 1);
    }

    return undefined;
  };
}
